/* eslint-disable @typescript-eslint/no-explicit-any */
// Exportar una lista del kit a Excel, CSV o PDF, como hacían las tablas viejas (ITable).
// Exporta TODO lo que cumple los filtros actuales (no solo la página visible): fetchAll trae las filas.
// columns: { label, value(row), width? (en caracteres), pdf?: false para dejarla fuera del PDF (ej. JSON largos) }.
// exceljs y jspdf se cargan solo al exportar, para no engordar la pantalla.
import React, { useState } from 'react';
import { Button } from './index';

const MAX_CELDA = 32000; // Excel no acepta más de 32.767 caracteres por celda
const texto = (v: any) => {
  if (v === null || v === undefined) return '';
  const t = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return t.length > MAX_CELDA ? `${t.slice(0, MAX_CELDA)}…` : t;
};
const descargar = (blob: Blob, nombre: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 0);
};
// Color del tema activo (cliente o Innobo) para el encabezado del archivo.
const colorTema = () => {
  const v =
    typeof document !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--ih-primary').trim() : '';
  return /^#[0-9a-f]{6}$/i.test(v) ? v : '#1a1a5b';
};

const aCsv = (cols: any[], filas: any[]) => {
  const esc = (v: any) => {
    const t = texto(v);
    return /[",\n\r;]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const lineas = [cols.map((c) => esc(c.label)).join(','), ...filas.map((r) => cols.map((c) => esc(c.value(r))).join(','))];
  // BOM para que Excel abra bien tildes y ñ
  return new Blob([`\uFEFF${lineas.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
};

const aExcel = async (cols: any[], filas: any[], hoja: string) => {
  const mod: any = await import('exceljs');
  const ExcelJS = mod.Workbook ? mod : mod.default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(hoja.slice(0, 31) || 'Data', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = cols.map((c, i) => ({ header: c.label, key: `c${i}`, width: c.width || 20 }));
  filas.forEach((r) => {
    const o: any = {};
    cols.forEach((c, i) => {
      o[`c${i}`] = texto(c.value(r));
    });
    ws.addRow(o);
  });
  const fondo = `FF${colorTema().slice(1).toUpperCase()}`;
  ws.getRow(1).eachCell((cell: any) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fondo } };
  });
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } };
  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

const aPdf = async (cols: any[], filas: any[], titulo: string) => {
  const mod: any = await import('jspdf');
  const JsPDF = mod.jsPDF || mod.default;
  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const ancho = doc.internal.pageSize.getWidth();
  const alto = doc.internal.pageSize.getHeight();
  const m = 28;
  const usable = ancho - m * 2;
  const pesos = cols.map((c) => c.width || 20);
  const total = pesos.reduce((a, b) => a + b, 0);
  const anchos = pesos.map((p) => (p / total) * usable);
  const color = colorTema();
  const fila = 16;
  let y = m;
  const encabezado = () => {
    doc.setFillColor(color);
    doc.rect(m, y, usable, fila, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    let x = m;
    cols.forEach((c, i) => {
      doc.text(doc.splitTextToSize(c.label, anchos[i] - 6)[0] || '', x + 3, y + 11);
      x += anchos[i];
    });
    y += fila;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(26, 26, 91);
  };
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 26, 91);
  doc.text(titulo, m, y + 10);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`${filas.length} rows · ${new Date().toLocaleString()}`, m, y + 24);
  y += 36;
  doc.setFontSize(7.5);
  encabezado();
  filas.forEach((r, n) => {
    if (y + fila > alto - m) {
      doc.addPage();
      y = m;
      encabezado();
    }
    if (n % 2 === 1) {
      doc.setFillColor(246, 246, 252);
      doc.rect(m, y, usable, fila, 'F');
    }
    let x = m;
    cols.forEach((c, i) => {
      const partes = doc.splitTextToSize(texto(c.value(r)).replace(/\s+/g, ' '), anchos[i] - 6);
      const t = partes.length > 1 ? `${String(partes[0]).replace(/.$/, '')}…` : partes[0] || '';
      doc.text(t, x + 3, y + 11);
      x += anchos[i];
    });
    y += fila;
  });
  return doc.output('blob');
};

export const MAX_PDF = 2000;

export const ExportMenu = ({ fileName, title, columns, fetchAll }: any) => {
  const [ocupado, setOcupado] = useState('');
  const [error, setError] = useState('');
  const correr = async (tipo: string) => {
    if (ocupado) return;
    setOcupado(tipo);
    setError('');
    try {
      const filas: any[] = (await fetchAll()) || [];
      const nombre = `${fileName}.${tipo === 'excel' ? 'xlsx' : tipo}`;
      if (tipo === 'csv') descargar(aCsv(columns, filas), nombre);
      if (tipo === 'excel') descargar(await aExcel(columns, filas, title || fileName), nombre);
      if (tipo === 'pdf')
        descargar(await aPdf(columns.filter((c: any) => c.pdf !== false), filas.slice(0, MAX_PDF), title || fileName), nombre);
    } catch (e: any) {
      setError(e?.message || 'Export failed');
    } finally {
      setOcupado('');
    }
  };
  const boton = (tipo: string, label: string, icon: string) => (
    <Button
      variant="secondary"
      icon={ocupado === tipo ? 'bx-loader-alt bx-spin' : icon}
      disabled={!!ocupado}
      title={`Download everything that matches the filters as ${label}`}
      onClick={() => correr(tipo)}
    >
      {label}
    </Button>
  );
  return (
    <>
      {boton('excel', 'Excel', 'bx-spreadsheet')}
      {boton('csv', 'CSV', 'bx-file')}
      {boton('pdf', 'PDF', 'bxs-file-pdf')}
      {error ? <span className="ihub-cell-sub" style={{ color: '#d92d20' }}>{error}</span> : null}
    </>
  );
};

// Trae todas las filas de una lista paginada: fetchPage(page, size) => { listData, CountItems }.
export const traerTodo = async (fetchPage: (page: number, size: number) => Promise<any>, size = 500, tope = 20000) => {
  const filas: any[] = [];
  for (let p = 1; filas.length < tope; p += 1) {
    const d = (await fetchPage(p, size)) || {};
    const lote = d.listData || [];
    filas.push(...lote);
    if (lote.length < size || filas.length >= (d.CountItems || 0)) break;
  }
  return filas.slice(0, tope);
};
